import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import * as Sentry from "@sentry/angular";
import { API_BASE_URL } from "src/swagger/api.client.generated";

export type NovaLogLevel = "Information" | "Warning" | "Error";
export type NovaLogTransport = "chat" | "voice";

export interface NovaLogEvent {
  timestamp: string;
  level: NovaLogLevel;
  event: string;
  sessionId?: string;
  transport?: NovaLogTransport;
  message?: string;
  properties?: Record<string, unknown>;
}

/** Keys that could carry conversation content or credentials. Never logged, whatever the caller passes. */
const REDACTED_KEYS = /^(text|transcript|content|message|messages|instructions|prompt|reply|clientsecret|secret|token|authorization|sdp)$/i;

const FLUSH_INTERVAL_MS = 5000;
const ERROR_FLUSH_DELAY_MS = 500;
const MAX_BUFFER = 100;
const MAX_BATCH = 50;
const MAX_STRING = 200;

/**
 * FLIP-662. Structured Nova diagnostics, sent in batches to `POST api/Nova/LogEvents` (stored in the
 * NovaLogs table) and, for warnings and errors, to Sentry tagged `feature:nova`.
 *
 * NEVER LOGS CONVERSATION CONTENT. Callers pass reasons, ids, counts, statuses and timings; keys that
 * look like text or credentials are stripped and strings are truncated as a backstop.
 *
 * NEVER THROWS AND NEVER SURFACES. A failed upload is dropped: logging must not become the reason a
 * voice session or chat turn fails, and retrying would only add load while the network is the very
 * thing being diagnosed. HIGH-FREQUENCY EVENTS (audio levels, transcript deltas, per-frame data
 * channel traffic) are deliberately not logged at all - log lifecycle and failures only.
 */
@Injectable({ providedIn: "root" })
export class NovaLogService {
  private buffer: NovaLogEvent[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private errorTimer: ReturnType<typeof setTimeout> | null = null;
  private activeSessionId: string | undefined;
  private activeTransport: NovaLogTransport | undefined;

  constructor(private http: HttpClient, @Inject(API_BASE_URL) private apiBaseUrl: string) {
    if (typeof window !== "undefined") {
      window.addEventListener("online", () => this.onNetworkChange(true));
      window.addEventListener("offline", () => this.onNetworkChange(false));
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
          this.flush();
        }
      });
    }
  }

  /**
   * Marks the session connectivity changes should be attributed to. Pass null when the session ends
   * so a later offline blip, unrelated to Nova, is not logged against a finished call.
   */
  setActiveSession(sessionId: string | null, transport: NovaLogTransport = "voice"): void {
    this.activeSessionId = sessionId ?? undefined;
    this.activeTransport = sessionId ? transport : undefined;
  }

  info(event: string, properties?: Record<string, unknown>, sessionId?: string, transport?: NovaLogTransport): void {
    this.add("Information", event, properties, sessionId, transport);
  }

  warn(event: string, properties?: Record<string, unknown>, sessionId?: string, transport?: NovaLogTransport): void {
    this.add("Warning", event, properties, sessionId, transport);
  }

  error(
    event: string,
    error?: unknown,
    properties?: Record<string, unknown>,
    sessionId?: string,
    transport?: NovaLogTransport
  ): void {
    this.add("Error", event, { ...properties, ...this.describeError(error) }, sessionId, transport, error);
  }

  /** Sends whatever is buffered now. Safe to call at any time. */
  flush(): void {
    this.clearTimers();
    if (this.buffer.length === 0) {
      return;
    }
    const events = this.buffer.splice(0, MAX_BATCH);
    try {
      this.http.post(`${this.apiBaseUrl}/api/Nova/LogEvents`, { events }).subscribe({
        next: () => undefined,
        error: () => undefined, // dropped on purpose - see the class comment
      });
    } catch {
      // never surfaces
    }
    if (this.buffer.length > 0) {
      this.scheduleFlush(FLUSH_INTERVAL_MS);
    }
  }

  private add(
    level: NovaLogLevel,
    event: string,
    properties: Record<string, unknown> | undefined,
    sessionId: string | undefined,
    transport: NovaLogTransport | undefined,
    error?: unknown
  ): void {
    try {
      const sid = sessionId ?? this.activeSessionId;
      const tr = transport ?? this.activeTransport;
      const clean = this.sanitise(properties);
      const message = typeof clean?.["reason"] === "string" ? (clean["reason"] as string) : undefined;

      if (this.buffer.length >= MAX_BUFFER) {
        this.buffer.shift(); // drop the oldest rather than grow without bound while offline
      }
      this.buffer.push({
        timestamp: new Date().toISOString(),
        level,
        event,
        sessionId: sid,
        transport: tr,
        message,
        properties: clean,
      });

      this.toSentry(level, event, clean, sid, tr, error);

      if (level === "Information") {
        this.scheduleFlush(FLUSH_INTERVAL_MS);
      } else {
        // Failures go out promptly: the page may be about to be reloaded or closed by the user.
        if (this.errorTimer === null) {
          this.errorTimer = setTimeout(() => this.flush(), ERROR_FLUSH_DELAY_MS);
        }
      }
    } catch {
      // never surfaces
    }
  }

  private toSentry(
    level: NovaLogLevel,
    event: string,
    properties: Record<string, unknown> | undefined,
    sessionId: string | undefined,
    transport: NovaLogTransport | undefined,
    error?: unknown
  ): void {
    try {
      Sentry.addBreadcrumb({
        category: "nova",
        message: event,
        level: level === "Error" ? "error" : level === "Warning" ? "warning" : "info",
        data: properties,
      });

      if (level !== "Error") {
        return;
      }
      Sentry.withScope((scope) => {
        scope.setTag("feature", "nova");
        scope.setTag("nova.source", "client");
        scope.setTag("nova.event", event);
        if (transport) {
          scope.setTag("nova.transport", transport);
        }
        if (sessionId) {
          scope.setTag("nova.session", sessionId);
        }
        scope.setContext("nova", { ...properties, online: this.isOnline() });
        if (error instanceof Error) {
          Sentry.captureException(error);
        } else {
          Sentry.captureMessage(`${event}`, "error");
        }
      });
    } catch {
      // never surfaces
    }
  }

  private onNetworkChange(online: boolean): void {
    if (!this.activeSessionId) {
      return;
    }
    const connection = (navigator as any).connection;
    const props = {
      online,
      effectiveType: connection?.effectiveType,
      downlinkMbps: connection?.downlink,
      rttMs: connection?.rtt,
    };
    if (online) {
      this.info("network.online", props);
    } else {
      this.warn("network.offline", props);
    }
  }

  private isOnline(): boolean | undefined {
    return typeof navigator !== "undefined" ? navigator.onLine : undefined;
  }

  private describeError(error: unknown): Record<string, unknown> {
    if (error === undefined || error === null) {
      return {};
    }
    const anyErr = error as any;
    return {
      errorName: anyErr?.name,
      reason: this.truncate(anyErr?.message ?? String(error)),
      status: anyErr?.status, // HttpErrorResponse
      online: this.isOnline(),
    };
  }

  private sanitise(properties?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!properties) {
      return undefined;
    }
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(properties)) {
      if (value === undefined || REDACTED_KEYS.test(key)) {
        continue;
      }
      out[key] = typeof value === "string" ? this.truncate(value) : value;
    }
    return Object.keys(out).length ? out : undefined;
  }

  private truncate(value: string): string {
    return value.length > MAX_STRING ? value.slice(0, MAX_STRING) : value;
  }

  private scheduleFlush(delayMs: number): void {
    if (this.flushTimer === null) {
      this.flushTimer = setTimeout(() => this.flush(), delayMs);
    }
  }

  private clearTimers(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.errorTimer !== null) {
      clearTimeout(this.errorTimer);
      this.errorTimer = null;
    }
  }
}
